import { Injectable } from "@angular/core";
import {
  AudioInputDeviceDisabled,
  VideoInputDeviceDisabled,
  VideoInputDeviceIsFlipped,
  VideoInputResolution
} from "@youm-front/account-device/domain";
import {
  AudioInputDeviceId,
  AudioOutputDeviceId,
  VideoInputDeviceId
} from "@youm-front/device/domain";
import { BehaviorSubject, Observable } from "rxjs";
import { distinctUntilChanged } from "rxjs/operators";

@Injectable()
export class AccountDeviceService {
  private static readonly AUDIO_INPUT_DEVICE_DISABLED =
    "accountDevice.audioInputDeviceDisabled";
  private static readonly AUDIO_INPUT_DEVICE_ID =
    "accountDevice.audioInputDeviceId";
  private static readonly AUDIO_OUTPUT_DEVICE_ID =
    "accountDevice.audioOutputDeviceId";
  private static readonly VIDEO_INPUT_DEVICE_DISABLED =
    "accountDevice.videoInputDeviceDisabled";
  private static readonly VIDEO_INPUT_DEVICE_ID =
    "accountDevice.videoInputDeviceId";
  private static readonly VIDEO_INPUT_DEVICE_IS_FLIPPED =
    "accountDevice.videoInputDeviceIsFlipped";
  private static readonly VIDEO_INPUT_RESOLUTION =
    "accountDevice.videoInputResolution";

  private readonly _audioInputDeviceDisabled$ =
    new BehaviorSubject<AudioInputDeviceDisabled>(
      !!localStorage.getItem(
        AccountDeviceService.AUDIO_INPUT_DEVICE_DISABLED
      ) as AudioInputDeviceDisabled
    );
  private readonly _audioInputDeviceId$ =
    new BehaviorSubject<null | AudioInputDeviceId>(
      localStorage.getItem(
        AccountDeviceService.AUDIO_INPUT_DEVICE_ID
      ) as null | AudioInputDeviceId
    );
  private readonly _audioOutputDeviceId$ =
    new BehaviorSubject<null | AudioOutputDeviceId>(
      localStorage.getItem(
        AccountDeviceService.AUDIO_OUTPUT_DEVICE_ID
      ) as null | AudioOutputDeviceId
    );
  private readonly _videoInputDeviceDisabled$ =
    new BehaviorSubject<VideoInputDeviceDisabled>(
      !!localStorage.getItem(
        AccountDeviceService.VIDEO_INPUT_DEVICE_DISABLED
      ) as VideoInputDeviceDisabled
    );
  private readonly _videoInputDeviceId$ =
    new BehaviorSubject<null | VideoInputDeviceId>(
      localStorage.getItem(
        AccountDeviceService.VIDEO_INPUT_DEVICE_ID
      ) as null | VideoInputDeviceId
    );
  private readonly _videoInputDeviceIsFlipped$ =
    new BehaviorSubject<VideoInputDeviceIsFlipped>(
      !!localStorage.getItem(
        AccountDeviceService.VIDEO_INPUT_DEVICE_IS_FLIPPED
      ) as VideoInputDeviceIsFlipped
    );
  private readonly _videoInputResolution$ =
    new BehaviorSubject<null | VideoInputResolution>(
      localStorage.getItem(
        AccountDeviceService.VIDEO_INPUT_RESOLUTION
      ) as null | VideoInputResolution
    );

  get audioInputDeviceDisabled(): AudioInputDeviceDisabled {
    return this._audioInputDeviceDisabled$.value;
  }

  get audioInputDeviceDisabled$(): Observable<AudioInputDeviceDisabled> {
    return this._audioInputDeviceDisabled$
      .asObservable()
      .pipe(distinctUntilChanged());
  }

  get audioInputDeviceId$(): Observable<null | AudioInputDeviceId> {
    return this._audioInputDeviceId$
      .asObservable()
      .pipe(distinctUntilChanged());
  }

  get audioOutputDeviceId$(): Observable<null | AudioOutputDeviceId> {
    return this._audioOutputDeviceId$
      .asObservable()
      .pipe(distinctUntilChanged());
  }

  get videoInputDeviceDisabled(): VideoInputDeviceDisabled {
    return this._videoInputDeviceDisabled$.value;
  }

  get videoInputDeviceDisabled$(): Observable<VideoInputDeviceDisabled> {
    return this._videoInputDeviceDisabled$
      .asObservable()
      .pipe(distinctUntilChanged());
  }

  get videoInputDeviceId$(): Observable<null | VideoInputDeviceId> {
    return this._videoInputDeviceId$
      .asObservable()
      .pipe(distinctUntilChanged());
  }

  get videoInputDeviceIsFlipped$(): Observable<VideoInputDeviceIsFlipped> {
    return this._videoInputDeviceIsFlipped$
      .asObservable()
      .pipe(distinctUntilChanged());
  }

  get videoInputResolution$(): Observable<null | VideoInputResolution> {
    return this._videoInputResolution$
      .asObservable()
      .pipe(distinctUntilChanged());
  }

  audioInputDeviceDisabledSet(
    audioInputDeviceDisabled: AudioInputDeviceDisabled
  ) {
    if (audioInputDeviceDisabled) {
      localStorage.setItem(
        AccountDeviceService.AUDIO_INPUT_DEVICE_DISABLED,
        "true"
      );
    } else {
      localStorage.removeItem(AccountDeviceService.AUDIO_INPUT_DEVICE_DISABLED);
    }

    this._audioInputDeviceDisabled$.next(audioInputDeviceDisabled);
  }

  audioInputDeviceIdSet(audioInputDeviceId: null | AudioInputDeviceId) {
    if (audioInputDeviceId) {
      localStorage.setItem(
        AccountDeviceService.AUDIO_INPUT_DEVICE_ID,
        audioInputDeviceId
      );
    } else {
      localStorage.removeItem(AccountDeviceService.AUDIO_INPUT_DEVICE_ID);
    }

    this._audioInputDeviceId$.next(audioInputDeviceId);
  }

  audioOutputDeviceIdSet(audioOutputDeviceId: null | AudioOutputDeviceId) {
    if (audioOutputDeviceId) {
      localStorage.setItem(
        AccountDeviceService.AUDIO_OUTPUT_DEVICE_ID,
        audioOutputDeviceId
      );
    } else {
      localStorage.removeItem(AccountDeviceService.AUDIO_OUTPUT_DEVICE_ID);
    }

    this._audioOutputDeviceId$.next(audioOutputDeviceId);
  }

  videoInputDeviceDisabledSet(
    videoInputDeviceDisabled: VideoInputDeviceDisabled
  ) {
    if (videoInputDeviceDisabled) {
      localStorage.setItem(
        AccountDeviceService.VIDEO_INPUT_DEVICE_DISABLED,
        "true"
      );
    } else {
      localStorage.removeItem(AccountDeviceService.VIDEO_INPUT_DEVICE_DISABLED);
    }

    this._videoInputDeviceDisabled$.next(videoInputDeviceDisabled);
  }

  videoInputDeviceIdSet(videoInputDeviceId: null | VideoInputDeviceId) {
    if (videoInputDeviceId) {
      localStorage.setItem(
        AccountDeviceService.VIDEO_INPUT_DEVICE_ID,
        videoInputDeviceId
      );
    } else {
      localStorage.removeItem(AccountDeviceService.VIDEO_INPUT_DEVICE_ID);
    }

    this._videoInputDeviceId$.next(videoInputDeviceId);
  }

  videoInputDeviceIsFlippedSet(
    videoInputDeviceIsFlipped: VideoInputDeviceIsFlipped
  ) {
    if (videoInputDeviceIsFlipped) {
      localStorage.setItem(
        AccountDeviceService.VIDEO_INPUT_DEVICE_IS_FLIPPED,
        "true"
      );
    } else {
      localStorage.removeItem(
        AccountDeviceService.VIDEO_INPUT_DEVICE_IS_FLIPPED
      );
    }

    this._videoInputDeviceIsFlipped$.next(videoInputDeviceIsFlipped);
  }

  videoInputResolutionSet(videoInputResolution: null | VideoInputResolution) {
    if (videoInputResolution) {
      localStorage.setItem(
        AccountDeviceService.VIDEO_INPUT_RESOLUTION,
        videoInputResolution
      );
    } else {
      localStorage.removeItem(AccountDeviceService.VIDEO_INPUT_RESOLUTION);
    }

    this._videoInputResolution$.next(videoInputResolution);
  }
}
